import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faChevronDown, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../utils/axiosConfig';
import './SearchableSelect.css';

const SearchableSelect = ({
  fetchUrl,
  params = {},
  displayFn,
  value,
  onChange,
  onResolve,
  placeholder = 'Search...',
  disabled = false,
  variant = 'form',
  pageSize = 20,
  minSearchLength = 2,
  debounceMs = 300,
  extraOptions = [],
  // When true, opening the picker lists the first page immediately instead of
  // waiting for typing. Pickers whose rows a user can't name from memory (VAT
  // rates: the operator knows "9%", not code 151) are unusable otherwise.
  listOnOpen = false,
  id,
  className = '',
  // Which field of the picked item becomes the value passed to onChange.
  // Default 'id' (FK semantics, resolvable via GET fetchUrl+value/). Set e.g.
  // 'account_number' to store a group-chart NUMBER instead of a row pk — in
  // that mode an unknown incoming value is displayed verbatim instead of
  // being resolved over the network (a non-pk can't be fetched by URL).
  valueKey = 'id',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  // What the SERVER says exists, and why the list is short. Both are
  // rendered: a picker showing 20 rows with nothing said about the rest is
  // read as "those are all of them", and a search that could not RUN was
  // being drawn as "No results" -- a refusal reported as an empty book.
  const [totalCount, setTotalCount] = useState(null);
  // Did the server say there is another page? `null` means it did not say.
  // Absent is NOT the same as "no": only a body that actually carries a
  // `next` key is making a promise about completeness.
  const [hasMorePages, setHasMorePages] = useState(null);
  // listOnOpen pickers load the next page when scrolled to the end, so a whole
  // list (Gritt, 2026-10-19: the 2,526 accounts) can be scrolled through.
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  // Bumped by every fresh search; a next page that comes back after one
  // belongs to the old list and is dropped.
  const generationRef = useRef(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [displayText, setDisplayText] = useState('');
  const [resolving, setResolving] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState(null);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const displayFnRef = useRef(displayFn);
  const onResolveRef = useRef(onResolve);
  const resolveCache = useRef({});

  displayFnRef.current = displayFn;
  onResolveRef.current = onResolve;

  const paramsKey = JSON.stringify(params);
  const extraOptionsKey = JSON.stringify(extraOptions);

  useEffect(() => {
    if (!value && value !== 0) {
      setDisplayText('');
      return;
    }

    const extras = extraOptions;
    const extra = extras.find(opt => String(opt.id) === String(value));
    if (extra) {
      setDisplayText(extra.display);
      return;
    }

    if (resolveCache.current[value]) {
      setDisplayText(resolveCache.current[value].text);
      onResolveRef.current?.(resolveCache.current[value].item);
      return;
    }

    if (valueKey !== 'id') {
      // Non-pk values can't be fetched by URL; show them verbatim.
      setDisplayText(String(value));
      return;
    }

    let cancelled = false;
    const resolveValue = async () => {
      setResolving(true);
      try {
        const response = await axiosInstance.get(`${fetchUrl}${value}/`);
        if (!cancelled) {
          const text = displayFnRef.current(response.data);
          setDisplayText(text);
          resolveCache.current[value] = { text, item: response.data };
          onResolveRef.current?.(response.data);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('SearchableSelect: Error resolving value:', error);
          setDisplayText(`ID: ${value}`);
        }
      }
      if (!cancelled) setResolving(false);
    };

    resolveValue();
    return () => { cancelled = true; };
  }, [value, fetchUrl, extraOptionsKey, valueKey]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = containerRef.current && containerRef.current.contains(event.target);
      const inDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);
      if (!inTrigger && !inDropdown) {
        setIsOpen(false);
        setSearchTerm('');
        setResults([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Position the fixed-position dropdown beneath the trigger and keep it
  // glued there if the user scrolls or resizes. position: fixed is what lets
  // the dropdown escape modal/overflow:auto containers.
  useEffect(() => {
    if (!isOpen) {
      setDropdownStyle(null);
      return undefined;
    }

    const updatePosition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const dropdownHeight = dropdownRef.current?.offsetHeight || 280;
      const openUpward = spaceBelow < dropdownHeight + 16 && rect.top > dropdownHeight + 16;

      setDropdownStyle({
        position: 'fixed',
        left: rect.left,
        top: openUpward ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
        minWidth: rect.width,
        width: variant === 'form' ? rect.width : undefined,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, results.length, variant]);

  useEffect(() => {
    setResults([]);
    setSearchTerm('');
  }, [paramsKey]);

  useEffect(() => {
    const hasSearch = searchTerm.length >= minSearchLength;
    // With listOnOpen the picker also fetches an unfiltered first page while
    // open, so it never sits empty waiting for a term the user can't guess.
    const shouldFetch = hasSearch || (listOnOpen && isOpen);

    if (!shouldFetch) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
      setResults([]);
      setTotalCount(null);
      setHasMorePages(null);
      setError(null);
      setLoading(false);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();

    generationRef.current += 1;
    setLoading(true);

    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const response = await axiosInstance.get(fetchUrl, {
          params: {
            ...(hasSearch ? { search: searchTerm } : {}),
            page: 1,
            page_size: pageSize,
            ...params,
          },
          signal: controller.signal,
        });
        const data = response.data;
        setResults(data.results || data || []);
        setPage(1);
        // DRF paginated endpoints carry the true total; the handful that
        // return a bare array do not, and we must not invent one -- null
        // means "the server did not say", and the copy below is worded so it
        // is still honest without it.
        setTotalCount(typeof data?.count === 'number' ? data.count : null);
        // DRF states the next page as a LINK: `next: null` on the last page is
        // the server saying "that is all of them". Reading it is the only way
        // to tell a full page that happens to be the whole answer from a full
        // page with hundreds behind it.
        setHasMorePages(
          data && typeof data === 'object' && !Array.isArray(data) && 'next' in data
            ? Boolean(data.next)
            : null
        );
        setError(null);
      } catch (err) {
        if (err.name === 'CanceledError' || err.name === 'AbortError') {
          // Superseded by a newer keystroke -- not a failure, and the newer
          // request owns the state from here.
          return;
        }
        console.error('SearchableSelect: Search error:', err);
        // This used to be swallowed, leaving the dropdown to fall through to
        // "No results for ...". A search the user is not allowed to run, or
        // one that never reached the server, then read as "that record does
        // not exist" -- and people went looking for a record they had, in
        // fact, simply been refused.
        setError(
          err?.response?.status === 403 || err?.response?.status === 401
            ? 'You do not have access to this list.'
            : 'This search could not be run — the list could not be loaded.'
        );
        setResults([]);
        setTotalCount(null);
        setHasMorePages(null);
      }
      setLoading(false);
    }, debounceMs);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [searchTerm, fetchUrl, paramsKey, pageSize, minSearchLength, debounceMs, listOnOpen, isOpen]);

  const loadMore = useCallback(async () => {
    if (!listOnOpen || loading || loadingMore || hasMorePages !== true) return;
    setLoadingMore(true);
    const generation = generationRef.current;
    try {
      const hasSearch = searchTerm.length >= minSearchLength;
      const response = await axiosInstance.get(fetchUrl, {
        params: {
          ...(hasSearch ? { search: searchTerm } : {}),
          page: page + 1,
          page_size: pageSize,
          ...params,
        },
      });
      const data = response.data;
      if (generation !== generationRef.current) return;   // a newer search owns the list
      const more = data.results || [];
      setResults(prev => {
        const seen = new Set(prev.map(r => r.id));
        return [...prev, ...more.filter(r => !seen.has(r.id))];
      });
      setPage(page + 1);
      setHasMorePages(Boolean(data && data.next));
      if (typeof data?.count === 'number') setTotalCount(data.count);
    } catch (err) {
      console.error('SearchableSelect: next page failed:', err);
      if (generation === generationRef.current) setError('The rest of the list could not be loaded.');
    } finally {
      setLoadingMore(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listOnOpen, loading, loadingMore, hasMorePages, searchTerm, minSearchLength, fetchUrl, page, pageSize, paramsKey]);

  const handleOptionsScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) loadMore();
  };

  // Reset keyboard highlight whenever the visible options change
  useEffect(() => {
    setHighlightIndex(-1);
  }, [results, extraOptionsKey, isOpen, loading]);

  const handleSelect = useCallback((item, isExtra = false) => {
    const text = isExtra ? item.display : displayFnRef.current(item);
    const selectedValue = isExtra ? item.id : (item[valueKey] ?? item.id);

    setDisplayText(text);
    if (!isExtra) {
      resolveCache.current[selectedValue] = { text, item };
    }

    onChange(selectedValue, item);
    setIsOpen(false);
    setSearchTerm('');
    setResults([]);
  }, [onChange, valueKey]);

  const handleClear = useCallback((e) => {
    e.stopPropagation();
    setDisplayText('');
    onChange(null, null);
    setSearchTerm('');
    setResults([]);
  }, [onChange]);

  const handleOpen = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [disabled]);

  const hasValue = value !== null && value !== undefined && value !== '';

  // The one honest line about how much of the answer is on screen -- or null,
  // when there is nothing honest to say.
  //
  // 2026-09-07: this used to fire on `results.length >= pageSize` alone, while
  // the server's own `count` sat in state unread. A search matching exactly one
  // full page of a COMPLETE set therefore told the operator the list had been
  // cut when it had not. That is worse than saying nothing: a warning that
  // cries wolf on ordinary searches is one people stop reading, and the
  // truncation that matters -- twenty of six hundred and thirteen -- goes past
  // them with it. So the server decides, and the length of the page is only
  // consulted where the endpoint states neither a count nor a next link.
  const truncationNote = (() => {
    if (results.length === 0) return null;
    // The server said outright that this is the last page.
    if (hasMorePages === false) return null;
    // The server's own total is not bigger than what we are showing.
    if (typeof totalCount === 'number' && totalCount <= results.length) return null;
    if (listOnOpen && hasMorePages === true) {
      const of = typeof totalCount === 'number' ? ` of ${totalCount}` : '';
      return `Showing ${results.length}${of} — scroll down for more, or type to narrow it down.`;
    }
    if (typeof totalCount === 'number' && totalCount > results.length) {
      return `Showing the first ${results.length} of ${totalCount} matches — keep typing to narrow it down.`;
    }
    // No count. Either the server said there is a next page, or it said
    // nothing at all and a brim-full page is the only clue we have.
    if (hasMorePages === true || results.length >= pageSize) {
      return `Showing the first ${results.length} matches — keep typing to narrow it down.`;
    }
    return null;
  })();

  // The combined list the dropdown actually renders: fetched results first,
  // then extraOptions. In the non-results branches ``results`` is empty, so
  // this stays in sync with every branch (nothing selectable while loading).
  const visibleOptions = loading
    ? []
    : [
        ...results.map((item) => ({ item, isExtra: false })),
        ...extraOptions.map((opt) => ({ item: opt, isExtra: true })),
      ];

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      return;
    }
    if (!isOpen || visibleOptions.length === 0) return;
    if (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();
      setHighlightIndex(prev => Math.min(prev + 1, visibleOptions.length - 1));
    } else if (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
      e.preventDefault();
      setHighlightIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      const entry = visibleOptions[highlightIndex];
      if (entry) handleSelect(entry.item, entry.isExtra);
    }
  };

  return (
    <div
      ref={containerRef}
      className={`searchable-select-container variant-${variant} ${isOpen ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''} ${className}`}
      id={id}
    >
      <div className="searchable-select-trigger" ref={triggerRef} onClick={handleOpen}>
        <span className={`searchable-select-display ${!displayText ? 'is-placeholder' : ''}`}>
          {resolving ? 'Loading...' : (displayText || placeholder)}
        </span>
        {hasValue && !disabled && (
          <button className="searchable-select-clear" onClick={handleClear} type="button" tabIndex={-1}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
        <span className="searchable-select-arrow">
          <FontAwesomeIcon icon={faChevronDown} />
        </span>
      </div>

      {isOpen && (
        <div className="searchable-select-dropdown" ref={dropdownRef} style={dropdownStyle || undefined}>
          <div className="searchable-select-search">
            <FontAwesomeIcon icon={loading ? faSpinner : faSearch} className={loading ? 'fa-spin' : ''} />
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={`Type ${minSearchLength}+ characters...`}
              autoFocus
            />
          </div>
          <div className="searchable-select-options" onScroll={handleOptionsScroll}>
            {!listOnOpen && searchTerm.length < minSearchLength && results.length === 0 ? (
              <>
                <div className="searchable-select-empty">
                  Start typing to search...
                </div>
                {extraOptions.map((opt, idx) => (
                  <div
                    key={opt.id}
                    ref={idx === highlightIndex ? (el) => el?.scrollIntoView({ block: 'nearest' }) : null}
                    className={`searchable-select-option extra-option ${idx === highlightIndex ? 'is-highlighted' : ''}`}
                    onClick={() => handleSelect(opt, true)}
                  >
                    {opt.display}
                  </div>
                ))}
              </>
            ) : loading ? (
              <div className="searchable-select-empty">
                Searching...
              </div>
            ) : error ? (
              <div className="searchable-select-empty searchable-select-error" role="alert">
                {error}
              </div>
            ) : results.length > 0 ? (
              <>
                {/* The picker asks for one page. Say so when it IS one page of
                    more -- and say nothing when the server told us this is the
                    whole answer. See `truncationNote` above. */}
                {truncationNote && (
                  <div className="searchable-select-empty searchable-select-more">
                    {truncationNote}
                  </div>
                )}
                {results.map((item, idx) => (
                  <div
                    key={item.id}
                    ref={idx === highlightIndex ? (el) => el?.scrollIntoView({ block: 'nearest' }) : null}
                    className={`searchable-select-option ${idx === highlightIndex ? 'is-highlighted' : ''}`}
                    onClick={() => handleSelect(item)}
                  >
                    {displayFnRef.current(item)}
                  </div>
                ))}
                {loadingMore && (
                  <div className="searchable-select-empty">Loading more...</div>
                )}
                {extraOptions.map((opt, i) => {
                  const idx = results.length + i;
                  return (
                    <div
                      key={opt.id}
                      ref={idx === highlightIndex ? (el) => el?.scrollIntoView({ block: 'nearest' }) : null}
                      className={`searchable-select-option extra-option ${idx === highlightIndex ? 'is-highlighted' : ''}`}
                      onClick={() => handleSelect(opt, true)}
                    >
                      {opt.display}
                    </div>
                  );
                })}
              </>
            ) : (
              <>
                <div className="searchable-select-empty">
                  {searchTerm ? `No results for "${searchTerm}"` : 'Nothing in this list yet.'}
                </div>
                {extraOptions.map((opt, idx) => (
                  <div
                    key={opt.id}
                    ref={idx === highlightIndex ? (el) => el?.scrollIntoView({ block: 'nearest' }) : null}
                    className={`searchable-select-option extra-option ${idx === highlightIndex ? 'is-highlighted' : ''}`}
                    onClick={() => handleSelect(opt, true)}
                  >
                    {opt.display}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
